import AddLocationOutlinedIcon from '@mui/icons-material/AddLocationOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { CircularProgress, ClickAwayListener, Divider, Fade, IconButton, InputAdornment, MenuItem, MenuList, Popper, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import PlacesAutocomplete from "react-places-autocomplete";
import { AuthContext } from '../../../contexts/AuthContext';
import { DashboardsContext } from '../../../contexts/DashboardsContext/DashboardsProvider';
import { addDashboardQueryFunc, getDashboardQueriesFunc } from '../../../lib/helper';
import { scrollbarStyle } from '../../../shared/dashboard';
import theme from '../../../theme';
import { Dashboard, DashboardQuery, DashboardQuerySource } from "../../../types/files";
import { FallbackLoading } from '../../templates/loader';

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 'max-content',
        minWidth: 350,
        maxWidth: 350,
        height: 'fit-content',
        marginTop: 16,
        padding: '12px 16px',
        overflowX: 'hidden',
        overflowY: 'auto',
        borderRadius: 12,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
        zIndex: 1500,
    },
    label: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: '500',
        maxWidth: '30ch',
        color: theme.colors.neutral['500'],
    },
    menu: {
        width: '100%',
        padding: 4,
        "&.Mui-focusVisible": {
            background: 'transparent !important',
        },
        "&:hover": {
            background: `rgb(246, 251, 251, 1)`,
        }
    },
    icon: {
        color: theme.colors.primary['500'],
        width: 24,
        height: 24,
    },
    inputField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 40,
            padding: '0 8px !important',
            borderRadius: 24,
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
}));

const SelectLocationModal: React.FC<{
    open: boolean,
    location: string,
    dashboard: Dashboard,
    anchorElement?: HTMLElement|null,
    onClose: () => void,
}> = ({ open, location, dashboard, anchorElement, onClose }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);
    const { setDashboards } = useContext(DashboardsContext);
    const [address, setAddress] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const locationRef = useRef<TextFieldProps>(null);;

    const handleSave = useCallback((location?: string) => {
        if (!dashboard)
            return;

        setAddress(location || address);
        setSaving(true);
        Promise.resolve(
            getDashboardQueriesFunc(dashboard!.id, 'Location')
        ).then((bulkQueryData: DashboardQuery[]) => {
            const newDashboard = { ...dashboard, updatedAt: new Date().toISOString() };
            const locationQuery = bulkQueryData.find(query =>
                query.dashboardId === dashboard.id && query.title === 'Location');

            if (!!locationQuery) {
                const newLocation = JSON.stringify({ answer: location || address, type: 'text' });

                addDashboardQueryFunc(
                    dashboard.id,
                    locationQuery.title,
                    userGroup,
                    locationQuery.query,
                    newLocation,
                    locationQuery.history,
                    locationQuery.order,
                    true,
                    DashboardQuerySource.MANUAL,
                ).then(() => {
                    mappedOverviewQueries.set(`${dashboard.id}:Location`, location || address);
                    setDashboardsQueries(prev => prev.map(dQ =>
                        (dQ.dashboardId === dashboard.id  && dQ.title === 'Location')
                            ? {...locationQuery, answer: newLocation} : dQ));
                    setDashboards(prev => prev.map(d => d.id === dashboard.id ? newDashboard : d));
                    setSaving(false);
                }).finally(onClose);
            }
        });
    // eslint-disable-next-line
    }, [dashboard, mappedOverviewQueries, userGroup, address, setDashboards, setDashboardsQueries]);

    useEffect(() => {
        setAddress(location);
    }, [location]);

    return (<>
        <Popper open={open} anchorEl={anchorElement} placement="bottom-start">
            <ClickAwayListener onClickAway={onClose}>
                <Stack className={classes.card} alignItems="flex-start" justifyContent="flex-start" width="100%">
                    <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSave}>
                        {({ getInputProps, suggestions, loading, getSuggestionItemProps }) => (<>
                            <TextField autoFocus
                                variant="outlined"
                                className={classes.inputField}
                                placeholder={'Search address ...'}
                                inputRef={locationRef}
                                sx={{ width: '100%' }}
                                disabled={saving}
                                InputProps={{
                                    ...getInputProps({}),
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ color: theme.palette.primary.main }}>
                                            <PlaceOutlinedIcon height={24} width={24} fill={theme.palette.primary.main} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end" >
                                            {saving ? (
                                                 <CircularProgress size={24} sx={{ color: theme.palette.primary.light }} />
                                            ) : (location !== address && !!address) && (
                                                <Fade in timeout={1000}>
                                                    <IconButton size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            handleSave();
                                                        }} sx={{ color: theme.palette.primary.main }}
                                                        disableRipple>
                                                        <AddLocationOutlinedIcon sx={{ width: 24, height: 24, }} />
                                                    </IconButton>
                                                </Fade>
                                            )}
                                        </InputAdornment>
                                )}} />
                            <MenuList sx={{ minWidth: 300 }}>
                                {loading ? (<FallbackLoading height="200px" />) : (location !== address && !!suggestions.length) && (
                                    <Stack direction="column" sx={{ ...scrollbarStyle, maxHeight: 256, overflowY: 'auto' }} >
                                        {suggestions.map(suggestion => (
                                            <Fragment key={suggestion.placeId}>
                                                <MenuItem className={classes.menu}
                                                    {...getSuggestionItemProps(suggestion)}>
                                                    <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="flex-start" width="100%" pl="4px">
                                                        <ExploreOutlinedIcon sx={{ width: 22, height: 22, fill: "rgb(163, 167, 172)" }} />
                                                        <Typography className={classes.label}>
                                                            {suggestion.description}
                                                        </Typography>
                                                    </Stack>
                                                </MenuItem>
                                                <Divider sx={{ width: '100%' }} />
                                            </Fragment>
                                        ))}
                                    </Stack>
                                )}
                            </MenuList>
                        </>)}
                    </PlacesAutocomplete>
                </Stack>
            </ClickAwayListener>
        </Popper>
    </>);
}

export default SelectLocationModal;